import { api } from '@common';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { useAuth } from '../useAuth';

export interface UserFormData {
  email: string;
  profile: string;
  tier?: string;
  physicalPerson?: {
    name: string;
    phone?: string;
    avatar?: string;
  };
}

export interface UserData {
  id: string;
  email: string;
  createdAt: string;
  updatedAt: string;
  profile: string;
  tier?: string;
  physicalPerson?: {
    id: string;
    name: string;
    avatar?: string;
    phone?: string;
  };
}

interface FetchUsersParams {
  page: number;
  limit: number;
  search?: string;
  orderBy?: string;
  orderType?: string;
}

interface UsersResponse {
  data: UserData[];
  pagination: {
    totalCount: number;
    page: number;
    pageSize: number;
  };
}

export const getUsers = (params: FetchUsersParams) => {
  const { isAuthenticated } = useAuth();
  return useQuery<UsersResponse>({
    queryKey: ['users', params],
    queryFn: async () => {
      try {
        // Try with parameters first, fallback to simple call if it fails
        let response;
        try {
          response = await api.get('/users', { params });
        } catch (error: any) {
          // If the API doesn't support parameters, make a simple call
          if (error.response?.status === 400) {
            console.warn('API does not support pagination parameters, falling back to simple call');
            response = await api.get('/users');
          } else {
            throw error;
          }
        }
        
        const responseData = response.data;
        
        // If response is a simple array, convert to paginated format
        if (Array.isArray(responseData)) {
          let filteredData = responseData;
          
          // Apply search filter if provided
          if (params.search) {
            const searchLower = params.search.toLowerCase();
            filteredData = responseData.filter((user: UserData) => 
              user.email.toLowerCase().includes(searchLower) ||
              user.physicalPerson?.name?.toLowerCase().includes(searchLower) ||
              user.profile?.toLowerCase().includes(searchLower)
            );
          }
          
          // Apply sorting if provided
          if (params.orderBy && params.orderType) {
            filteredData.sort((a: any, b: any) => {
              const aValue = a[params.orderBy!];
              const bValue = b[params.orderBy!];
              
              if (aValue < bValue) return params.orderType === 'asc' ? -1 : 1;
              if (aValue > bValue) return params.orderType === 'asc' ? 1 : -1;
              return 0;
            });
          }
          
          // Apply pagination
          const startIndex = (params.page - 1) * params.limit;
          const endIndex = startIndex + params.limit;
          const paginatedData = filteredData.slice(startIndex, endIndex);
          
          return {
            data: paginatedData,
            pagination: {
              totalCount: filteredData.length,
              page: params.page,
              pageSize: params.limit
            }
          };
        }
        
        // If response has pagination structure, use it as is
        if (!responseData.pagination) {
          return {
            data: Array.isArray(responseData) ? responseData : responseData.data || [],
            pagination: {
              totalCount: Array.isArray(responseData) ? responseData.length : (responseData.data || []).length,
              page: params.page || 1,
              pageSize: params.limit || 10
            }
          };
        }

        return responseData;
      } catch (error) {
        console.error('Error fetching users:', error);
        message.error('Unable to load users list');
        throw error;
      }
    },
    enabled: isAuthenticated,
  });
};

export const useUsers = () => {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();

  // Get a specific user by ID
  const getUser = (id: string) => {
    return useQuery<UserData>({
      queryKey: ['user', id],
      queryFn: async () => {
        try {
          const response = await api.get(`/users/${id}`);
          return response.data;
        } catch (error) {
          console.error(`Error fetching user ${id}:`, error);
          message.error('Unable to load user data');
          throw error;
        }
      },
      enabled: !!id && isAuthenticated,
    });
  };

  const createUser = useMutation({
    mutationFn: async (userData: UserFormData) => {
      try {
        const formattedData = {
          email: userData.email,
          profile: userData.profile,
          tier: userData.tier,
          physicalPerson: userData.physicalPerson && {
            name: userData.physicalPerson.name,
            phone: userData.physicalPerson.phone || ''

          }
        };
        const response = await api.post('/users', formattedData);
        message.success('User created successfully');
        return response.data;
      } catch (error: any) {
        console.error('Error creating user:', error);

        // Log the exact error response for debugging
        if (error.response?.data) {
          console.error('API error response:', error.response.data);
          console.error('Error status:', error.response.status);
          console.error('Error headers:', error.response.headers);
        }

        if (error.response?.data?.message) {
          const errorMsg = Array.isArray(error.response.data.message)
            ? error.response.data.message.join(', ')
            : error.response.data.message;
          message.error(`Unable to create user: ${errorMsg}`);
        } else {
          message.error('Unable to create user');
        }

        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  // Update an existing user
  const updateUser = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UserFormData }) => {
      try {
        const formattedData = {
          email: data.email,
          profile: data.profile,
          tier: data.tier,
          physicalPerson: data.physicalPerson && {
            name: data.physicalPerson.name,
            phone: data.physicalPerson.phone || ''
          }
        };

        const response = await api.patch(`/users/${id}`, formattedData);
        message.success('User updated successfully');
        return response.data;
      } catch (error: any) {
        console.error(`Error updating user ${id}:`, error);

        // Log the exact error response for debugging
        if (error.response?.data) {
          console.error('API error response:', error.response.data);
          console.error('Error status:', error.response.status);
          console.error('Error headers:', error.response.headers);
        }

        // Display more detailed error message if available
        if (error.response?.data?.message) {
          const errorMsg = Array.isArray(error.response.data.message)
            ? error.response.data.message.join(', ')
            : error.response.data.message;
          message.error(`Unable to update user: ${errorMsg}`);
        } else {
          message.error('Unable to update user');
        }

        throw error;
      }
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      queryClient.invalidateQueries({ queryKey: ['user', variables.id] });
    },
  });

  // Delete a user
  const deleteUser = useMutation({
    mutationFn: async (id: string) => {
      try {
        const response = await api.delete(`/users/${id}`);
        message.success('User deleted successfully');
        return response.data;
      } catch (error: any) {
        console.error(`Error deleting user ${id}:`, error);

        if (error.response?.data) {
          console.error('API error response:', error.response.data);
          console.error('Error status:', error.response.status);
        }

        if (error.response?.data?.message) {
          const errorMsg = Array.isArray(error.response.data.message)
            ? error.response.data.message.join(', ')
            : error.response.data.message;
          message.error(`Unable to delete user: ${errorMsg}`);
        } else {
          message.error('Unable to delete user');
        }

        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  // Update current user's profile
  const updateProfile = useMutation({
    mutationFn: async (data: UserFormData['physicalPerson']) => {
      try {
        const formattedData = {
          name: data?.name,
          phone: data?.phone || '',
          avatar: data?.avatar || ''
        };

        const response = await api.patch('/users/profile', formattedData);
        message.success('Profile updated successfully');
        return response.data;
      } catch (error: any) {
        console.error('Error updating profile:', error);

        if (error.response?.data) {
          console.error('API error response:', error.response.data);
          console.error('Error status:', error.response.status);
        }

        if (error.response?.data?.message) {
          const errorMsg = Array.isArray(error.response.data.message)
            ? error.response.data.message.join(', ')
            : error.response.data.message;
          message.error(`Unable to update profile: ${errorMsg}`);
        } else {
          message.error('Unable to update profile');
        }

        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
  });

  return {
    getUser,
    createUser,
    updateUser,
    deleteUser,
    updateProfile,
  };
};
